import { useEffect, useState } from "react";
import { NON_DASHBOARD_PAGES, SIDEBAR_ITEMS } from "enums";
import { Link, useLocation } from "react-router-dom";
import ProfileDropdown from "components/profile-dropdown";
import { Menu, X } from "lucide-react";
import { cn } from "components/ui/cn";

const MobileHeader = () => {
   const location = useLocation();
   const hideProfile = NON_DASHBOARD_PAGES.includes(location.pathname);

   const [showSidebar, setShowSidebar] = useState(false);

   const justifyClass = hideProfile ? "justify-center" : "justify-between";
   const activeClass = "border-accent-light bg-white/12 text-white font-medium";

   useEffect(() => {
      if (showSidebar) {
         document.body.style.overflowY = "hidden";
      } else {
         document.body.style.overflowY = "auto";
      }
   }, [showSidebar]);

   // Close sidebar when navigating to a new page
   useEffect(() => {
      setShowSidebar(false);
   }, [location.pathname]);

   return (
      <>
         {/* Mobile Header Bar */}
         <div
            className={`fixed top-0 left-0 z-40 flex w-full items-center border-b border-solid border-rule bg-white px-4 py-3 ${justifyClass} lg:hidden`}
         >
            <div className="flex items-center gap-2">
               {!hideProfile && (
                  <button
                     type="button"
                     onClick={() => setShowSidebar(true)}
                     className="rounded-ns-sm p-2 text-muted transition-colors duration-ui ease-ui hover:bg-shade-50 hover:text-accent focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent"
                     aria-label="Open menu"
                  >
                     <Menu size={20} />
                  </button>
               )}

               <Link to="/">
                  <img alt="NirvaScribe" src="/brand-lockup.png" className="h-7 w-auto" />
               </Link>
            </div>

            
         </div>

         {/* Spacer for fixed header */}
         <div className="h-14 lg:hidden" />

         {/* Mobile Sidebar Drawer */}
         {!hideProfile && (
            <>
               {/* Backdrop/Overlay */}
               {showSidebar && (
                  <div
                     className="fixed inset-0 z-40 bg-ink/45 transition-opacity lg:hidden"
                     onClick={() => setShowSidebar(false)}
                     aria-hidden="true"
                  />
               )}

               {/* Sidebar */}
               <div
                  style={{
                     transform: !showSidebar ? "translateX(-100%)" : "translateX(0%)",
                  }}
                  className="fixed top-0 left-0 z-50 flex h-full w-[280px] flex-col bg-footer-ground shadow-pop transition-transform duration-300 ease-in-out"
               >
                  {/* Grain, as on the desktop rail. Standards §5. */}
                  <span
                     aria-hidden="true"
                     className="pointer-events-none absolute inset-0 bg-grain mix-blend-overlay"
                     style={{ opacity: "var(--ns-grain-opacity)" }}
                  />

                  {/* Header. The mark plus a live wordmark, not the lockup — the
                      lockup's wordmark is dark and vanishes here (§8). */}
                  <div className="relative flex flex-shrink-0 items-center justify-between border-b border-solid border-white/12 px-4 py-4">
                     <Link to="/" onClick={() => setShowSidebar(false)} className="flex items-center gap-2">
                        <img alt="" src="/brand-mark.png" className="h-7 w-7" />
                        <span className="font-display text-ui-md font-semibold tracking-[-0.01em] text-white">
                           NirvaScribe
                        </span>
                     </Link>
                     <button
                        onClick={() => setShowSidebar(false)}
                        className="rounded-ns-sm p-1.5 text-white/70 transition-colors duration-ui ease-ui hover:bg-white/12 hover:text-white focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent-light"
                        aria-label="Close menu"
                     >
                        <X size={18} />
                     </button>
                  </div>

                  {/* Navigation */}
                  <div className="relative flex-1 overflow-y-auto scrollbar-thin">
                     <nav className="py-2">
                        <ul className="space-y-0.5">
                           {SIDEBAR_ITEMS(true).map((item) => (
                              <li key={item.to}>
                                 <Link
                                    to={item.to}
                                    onClick={() => setShowSidebar(false)}
                                    className={cn(
                                       "flex items-center gap-3 border-l-[3px] border-solid py-3 pl-6 pr-4",
                                       "transition-colors duration-ui ease-ui",
                                       location.pathname === item.to
                                          ? activeClass
                                          : "border-transparent text-white/70 hover:bg-white/8 hover:text-white"
                                    )}
                                 >
                                    <span className="flex-shrink-0">
                                       {item.icon}
                                    </span>
                                    <span className="font-body text-ui-sm">
                                       {item.label}
                                    </span>
                                 </Link>
                              </li>
                           ))}
                        </ul>
                     </nav>
                  </div>

                  {/* Bottom Section */}
                  <div className="relative flex-shrink-0 border-t border-solid border-white/12">
                     {/* Profile Dropdown */}
                     <div className="px-3 py-3">
                        <ProfileDropdown />
                     </div>
                  </div>
               </div>
            </>
         )}
      </>
   );
};

export default MobileHeader;